import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { marked } from '../../vendor/marked.js';
import { renderOnboardingSteps } from './onboarding-steps.js';
import { printAgreement } from './print-agreement.js';

/**
 * Blocks a freelancer until they have accepted the current version of every
 * required platform document.
 *
 * Each document gets its own affirmative accept action rather than one blanket
 * checkbox — separate assent is what makes each document independently
 * provable. The document bodies come from the API (embedded in the binary), so
 * the markdown rendered here is our own text, never user input.
 *
 * The documents are shown one per page, in order: the first one not yet
 * accepted fills the page, and accepting it moves on to the next. Accepted
 * documents can be reread on the Agreements page. The step indicator shows
 * the freelancer's whole onboarding (`steps`, from the app), which goes on to
 * their master services agreement.
 */
class AgreementGate extends BaseComponent {
    static properties = {
        steps: { type: Array, attribute: false },
        _agreements: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _accepting: { type: String, state: true },
    };

    static styles = css`
        /* The gate fills the height the app gives it: the document takes what
           is left after the heading and step indicator, and only its text
           scrolls, so the accept button stays in view. */
        :host { display: flex; flex-direction: column; min-height: 0; }
        :host > * { flex: none; }
        .gate-header h4 { margin: 0 0 0.25rem; font-size: 1.5rem; font-weight: 600; }
        .gate-intro { color: #666; font-size: 0.9rem; margin-bottom: 1.25rem; }
        .doc {
            border: 1px solid #e5e5e5; border-radius: 8px; background: #fff;
            display: flex; flex-direction: column; margin-bottom: 1rem;
        }
        :host > .doc { flex: 1 1 auto; min-height: 12rem; }
        .doc-header {
            display: flex; justify-content: space-between; align-items: center; gap: 1rem;
            padding: 0.75rem 1.25rem; background: #fafafa; border-bottom: 1px solid #eee;
            border-radius: 8px 8px 0 0;
        }
        .doc-header .title { font-weight: 600; }
        .doc-body {
            flex: 1 1 auto; min-height: 0; overflow-y: auto;
            padding: 1rem 1.25rem; font-size: 0.9rem; line-height: 1.55;
        }
        .doc-body h1 { font-size: 1.25rem; margin-top: 0; }
        .doc-body h2 { font-size: 1.05rem; margin-top: 1.25rem; }
        .doc-footer {
            padding: 0.75rem 1.25rem; border-top: 1px solid #eee; text-align: right;
            border-radius: 0 0 8px 8px;
        }
    `;

    constructor() {
        super();
        this._agreements = [];
        this._loading = true;
        this._error = null;
        this._accepting = null;
        /** @type {Array<{ key: string, label: string }>} */
        this.steps = [];
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._error = null;
        try {
            const data = await api.get('/api/agreements');
            this._agreements = data.agreements || [];
            if (data.all_accepted) {
                this.dispatchEvent(
                    new CustomEvent('agreements-accepted', { bubbles: true, composed: true }),
                );
            }
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _accept(agreement) {
        this._error = null;
        this._accepting = agreement.key;
        try {
            await api.post(`/api/agreements/${agreement.key}/accept`, {
                version: agreement.version,
            });
        } catch (err) {
            this._error = err.message;
            this._accepting = null;
            return;
        }
        // The acceptance is committed once the POST resolves. Refresh outside the
        // try so a failed reload reports its own error (_load sets _error itself)
        // instead of reading as a failed acceptance and inviting the user to
        // re-accept something that already took. The button stays disabled until
        // the next document has replaced this one.
        await this._load();
        this._accepting = null;
        // The next document reuses the scroll box, so start it at the top.
        await this.updateComplete;
        const body = this.renderRoot.querySelector('.doc-body');
        if (body) body.scrollTop = 0;
    }

    _print(a) {
        this._error = null;
        try {
            printAgreement(a);
        } catch (err) {
            this._error = err.message;
        }
    }

    render() {
        if (this._loading) {
            return html`
                <div class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                </div>
            `;
        }

        const doc = this._agreements.find((a) => !a.accepted);

        return html`
            <div class="gate-header"><h4>Before you get started</h4></div>
            <p class="gate-intro">
                Please review and accept each of these. They cover your use of this platform and how
                we handle your information. They do not change your Master Services Agreement, which
                is signed separately.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                doc
                    ? html`
                ${renderOnboardingSteps(this.steps, doc.key)}
                <div class="doc">
                    <div class="doc-header">
                        <span class="title">${doc.title}</span>
                        <button class="btn btn-outline-secondary btn-sm" @click="${() => this._print(doc)}">
                            <i class="bi bi-printer"></i> Print
                        </button>
                    </div>
                    <div class="doc-body" .innerHTML="${marked.parse(doc.body || '')}"></div>
                    <div class="doc-footer">
                        <button
                            class="btn btn-primary"
                            ?disabled="${this._accepting === doc.key}"
                            @click="${() => this._accept(doc)}"
                        >
                            ${this._accepting === doc.key ? 'Recording…' : `I accept the ${doc.title}`}
                        </button>
                    </div>
                </div>
            `
                    : ''
            }
        `;
    }
}

customElements.define('agreement-gate', AgreementGate);
